import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { ArrowRight, BookOpen, GraduationCap, Anchor, Radio } from "lucide-react";

const NAVY_PORTRAIT = "https://customer-assets.emergentagent.com/job_morse-chronicles/artifacts/tfncxvd5_Don%20Inbody%20Navy.jpg";

export default function About() {
  useEffect(() => {
    document.title = "Donald Stephen Inbody — Author, Naval Historian & Political Scientist";
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Person",
      "name": "Donald S. Inbody",
      "jobTitle": "Author, Political Scientist, Naval Officer (Retired)",
      "alumniOf": [
        { "@type": "CollegeOrUniversity", "name": "University of Texas at Austin" }
      ],
      "description": "Retired Captain, U.S. Navy. PhD in Government, UT Austin. Senior Lecturer in Political Science, Texas State University. Author of The Soldier Vote (Palgrave Macmillan).",
    });
    document.head.appendChild(script);
    return () => document.head.removeChild(script);
  }, []);

  return (
    <div className="bg-[#F5F0E8]">
      <SEO
        title="About Donald Stephen Inbody"
        description="Donald Stephen Inbody — retired U.S. Navy Captain, political scientist, and author of The Soldier Vote (Palgrave Macmillan) and the forthcoming Morse Chronicles naval fiction series. Not affiliated with InBody Co., Ltd. (inbody.com)."
        canonical="https://inbody.net/about"
        keywords="Donald Stephen Inbody biography, naval historian, political scientist, University of Texas"
      />
      <Navbar />
      <section className="pt-16 border-b border-[#0A1628]/10" data-testid="about-hero-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 items-end">

            {/* Credentials side */}
            <div className="py-20 lg:py-24 pr-0 lg:pr-16">
              <p className="overline mb-4">About the Author</p>
              <h1
                data-testid="about-page-title"
                className="font-serif text-5xl sm:text-6xl font-black text-[#0A1628] leading-tight mb-6"
              >
                Donald S.<br />Inbody
              </h1>
              <div className="w-16 h-0.5 bg-[#C5A059] mb-8" />

              {/* Key credentials */}
              <div className="space-y-5">
                {[
                  {
                    icon: Anchor,
                    label: "Military Service",
                    value: "Captain, U.S. Navy (Retired)",
                    detail: "28 years · Commanded USS Duluth (LPD-6)",
                  },
                  {
                    icon: GraduationCap,
                    label: "Education",
                    value: "PhD in Government",
                    detail: "University of Texas at Austin",
                  },
                  {
                    icon: BookOpen,
                    label: "Academia",
                    value: "Senior Lecturer, Political Science",
                    detail: "Texas State University · 11 years",
                  },
                  {
                    icon: Radio,
                    label: "Amateur Radio",
                    value: "ADØK",
                    detail: "Licensed amateur radio operator",
                  },
                ].map(({ icon: Icon, label, value, detail }) => (
                  <div key={label} className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-[#0A1628]/8 border border-[#0A1628]/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Icon size={14} className="text-[#0A1628]" />
                    </div>
                    <div>
                      <div className="text-[#2D3142]/50 text-xs uppercase tracking-widest mb-0.5">{label}</div>
                      <div className="text-[#0A1628] text-sm font-semibold">{value}</div>
                      <div className="text-[#2D3142]/60 text-xs">{detail}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Portrait side */}
            <div className="flex items-end justify-center lg:justify-end">
              <img
                src={NAVY_PORTRAIT}
                alt="Donald S. Inbody — Captain, U.S. Navy (Retired)"
                data-testid="about-navy-portrait"
                className="w-72 lg:w-80 object-cover object-top"
                style={{ maxHeight: "520px", objectPosition: "top center" }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── Biography ── */}
      <section data-testid="about-bio-section" className="py-24 bg-[#F5F0E8]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="overline mb-3">Biography</p>
            <h2 className="font-serif text-3xl font-bold text-[#0A1628] mb-10">
              Sailor, Scholar &amp; Storyteller
            </h2>

            {/* Short bio — provided by author */}
            <div className="space-y-6 text-[#2D3142] leading-[1.9] text-base">
              <p>
                My naval career spans twenty-eight years, culminating in my retirement as a Captain in the United States Navy, having commanded USS Duluth (LPD-6). This experience provides the technical foundation and cultural authenticity for the wardroom dynamics and maritime operations depicted in <em>Wake of the Falcon</em>.
              </p>
              <p>
                In my civilian life, I earned a PhD in Government from the University of Texas at Austin and taught for eleven years as a Senior Lecturer in Political Science at Texas State University. I am also the author of the non-fiction book <em>The Soldier Vote</em> (Palgrave Macmillan) as well as articles and book chapters.
              </p>
            </div>

            {/* Spacer for future expansion */}
            <div className="mt-10 border-t border-[#0A1628]/10 pt-10">
              <p className="text-[#2D3142]/40 text-sm italic">
                Extended biography coming soon.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Career Sections ── */}
      <section className="py-16 bg-white" data-testid="about-career-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">

            {/* Military */}
            <div className="border border-[#0A1628]/10 p-8">
              <div className="w-10 h-10 bg-[#0A1628] flex items-center justify-center mb-5">
                <Anchor size={18} className="text-[#C5A059]" />
              </div>
              <h3 className="font-serif text-xl font-bold text-[#0A1628] mb-3">Naval Service</h3>
              <div className="space-y-3 text-sm text-[#2D3142]">
                <div className="border-l-2 border-[#C5A059] pl-4">
                  <div className="font-semibold text-[#0A1628]">Retired Captain, U.S. Navy</div>
                  <div className="text-[#2D3142]/60 text-xs">28 Years of Service</div>
                </div>
                <div className="border-l-2 border-[#0A1628]/20 pl-4">
                  <div className="font-semibold text-[#0A1628]">Commanding Officer</div>
                  <div className="text-[#2D3142]/60 text-xs">USS Duluth (LPD-6)</div>
                </div>
                <p className="text-[#2D3142]/70 text-xs leading-relaxed pt-2">
                  Naval service spanning amphibious operations, command at sea, and leadership across multiple deployments and assignments.
                </p>
              </div>
            </div>

            {/* Academic */}
            <div className="border border-[#0A1628]/10 p-8">
              <div className="w-10 h-10 bg-[#0A1628] flex items-center justify-center mb-5">
                <GraduationCap size={18} className="text-[#C5A059]" />
              </div>
              <h3 className="font-serif text-xl font-bold text-[#0A1628] mb-3">Academia</h3>
              <div className="space-y-3 text-sm text-[#2D3142]">
                <div className="border-l-2 border-[#C5A059] pl-4">
                  <div className="font-semibold text-[#0A1628]">PhD in Government</div>
                  <div className="text-[#2D3142]/60 text-xs">University of Texas at Austin</div>
                </div>
                <div className="border-l-2 border-[#0A1628]/20 pl-4">
                  <div className="font-semibold text-[#0A1628]">Senior Lecturer, Political Science</div>
                  <div className="text-[#2D3142]/60 text-xs">Texas State University · 11 Years</div>
                </div>
                <p className="text-[#2D3142]/70 text-xs leading-relaxed pt-2">
                  Research focus on civil-military relations, military voting rights, and the intersection of national security and democratic governance.
                </p>
              </div>
            </div>

            {/* Publications */}
            <div className="border border-[#0A1628]/10 p-8">
              <div className="w-10 h-10 bg-[#0A1628] flex items-center justify-center mb-5">
                <BookOpen size={18} className="text-[#C5A059]" />
              </div>
              <h3 className="font-serif text-xl font-bold text-[#0A1628] mb-3">Publications</h3>
              <div className="space-y-3 text-sm text-[#2D3142]">
                <div className="border-l-2 border-[#C5A059] pl-4">
                  <div className="font-semibold text-[#0A1628]">The Soldier Vote</div>
                  <div className="text-[#2D3142]/60 text-xs">Palgrave Macmillan</div>
                </div>
                <div className="border-l-2 border-[#0A1628]/20 pl-4">
                  <div className="font-semibold text-[#0A1628]">Articles &amp; Book Chapters</div>
                  <div className="text-[#2D3142]/60 text-xs">Political Science · Military Studies</div>
                </div>
                <div className="border-l-2 border-[#0A1628]/20 pl-4">
                  <div className="font-semibold text-[#0A1628]">Wake of the Falcon</div>
                  <div className="text-[#2D3142]/60 text-xs italic">Upcoming Novel</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Links ── */}
      <section className="py-16 bg-[#F5F0E8]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-wrap gap-4">
          <Link
            to="/books"
            data-testid="about-books-cta"
            className="inline-flex items-center gap-2 bg-[#0A1628] text-[#F5F0E8] px-6 py-3 text-sm font-semibold hover:bg-[#2D3142] transition-colors"
          >
            View Publications <ArrowRight size={14} />
          </Link>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 border border-[#0A1628] text-[#0A1628] px-6 py-3 text-sm font-semibold hover:bg-[#0A1628] hover:text-[#F5F0E8] transition-colors"
          >
            Read the Blog <ArrowRight size={14} />
          </Link>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 border border-[#0A1628]/40 text-[#2D3142] px-6 py-3 text-sm font-semibold hover:border-[#0A1628] transition-colors"
          >
            Get in Touch <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
